import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { exportConfigsApi } from '../../api/exportConfigs';
import { overlaysApi } from '../../api/overlays';
import { ExportConfig, Overlay, OVERLAY_SIZE_LABELS } from '../../types';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { Modal } from '../UI/Modal';
import { clayTapScale } from '../../constants/animations';
import styles from './ExportConfigsPanel.module.css';

interface ExportConfigsPanelProps {
  eventId: string;
  canManage?: boolean;
}

/** 'bottom-right' → 'Bottom Right' */
const prettyAnchor = (anchor: string) =>
  anchor.split('-').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

/** Render an adjustment value with a leading sign, trimming float noise. */
const fmtSigned = (n: number) => {
  const rounded = Math.round(n * 100) / 100;
  if (rounded === 0) return '0';
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

export const ExportConfigsPanel: React.FC<ExportConfigsPanelProps> = ({ eventId, canManage = true }) => {
  const [configs, setConfigs] = useState<ExportConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [detailConfig, setDetailConfig] = useState<ExportConfig | null>(null);

  // Overlay names/previews are a best-effort enhancement for the details view.
  const overlayById = useMemo(
    () => new Map(overlays.map((o) => [o._id, o])),
    [overlays],
  );

  const loadConfigs = async () => {
    try {
      setLoading(true);
      console.log('[ExportConfigsPanel] loading presets for event:', eventId);
      const data = await exportConfigsApi.list(eventId);
      console.log('[ExportConfigsPanel] loaded', data.length, 'presets:', data);
      setConfigs(data);
    } catch (err) {
      console.error('[ExportConfigsPanel] Failed to load export configs:', err);
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
    overlaysApi.list(eventId).then(setOverlays).catch(() => { /* overlay names are best-effort */ });
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id: string) => {
    try {
      await exportConfigsApi.delete(eventId, id);
      setConfigs((prev) => prev.filter((c) => c._id !== id));
      toast.success('Config deleted');
    } catch {
      toast.error('Failed to delete config');
    }
  };

  return (
    <div className={styles['panel']}>

      {/* Header */}
      <div className={styles['header']}>
        <div>
          <h2 className={styles['header-title']}>Export Presets</h2>
          <p className={styles['header-subtitle']}>
            Saved processing presets for photos. Apply these when editing photos in the gallery.
          </p>
        </div>
        <motion.button
          onClick={loadConfigs}
          className={styles['refresh-btn']}
          whileTap={{ scale: 0.9 }}
          disabled={loading}
          title="Refresh presets"
        >
          ↺
        </motion.button>
      </div>

      <div className={styles['body']}>
      {loading ? (
        <motion.div
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          Loading...
        </motion.div>
      ) : configs.length === 0 ? (
        <motion.div
          className={styles['empty-state']}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles['empty-inner']}>
            <div className={styles['empty-illustration']}>
              <MachhaCharacter state="Empty" part="body" size={140} />
            </div>
            <div>
              <p className={styles['empty-title']}>No presets saved yet.</p>
              <p className={styles['empty-description']}>Save a preset from the photo editor's Export step to reuse it here.</p>
            </div>
          </div>
        </motion.div>
      ) : (
        <div className={styles['config-list']}>
          <AnimatePresence>
            {configs.map((config, index) => (
              <motion.div
                key={config._id}
                initial={{ opacity: 0, y: 16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25, delay: index * 0.05 }}
                className={styles['config-item']}
                whileHover={{
                  x: 3,
                  backgroundColor: 'rgba(239,68,68,0.06)',
                  boxShadow: '0 2px 12px rgba(239,68,68,0.08)',
                }}
                role="button"
                tabIndex={0}
                aria-label={`View details for preset ${config.name}`}
                onClick={() => setDetailConfig(config)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDetailConfig(config);
                  }
                }}
              >
                <div className={styles['config-content']}>
                  <p className={styles['config-name']}>{config.name}</p>
                  <div className={styles['config-tags']}>
                    {config.overlay && (
                      <motion.span
                        className={styles['config-tag']}
                        whileHover={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#ef4444' }}
                        transition={{ duration: 0.15 }}
                      >
                        Overlay
                      </motion.span>
                    )}
                    {config.selectedPlatforms.length > 0 && (
                      <motion.span
                        className={styles['config-tag']}
                        whileHover={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#ef4444' }}
                        transition={{ duration: 0.15 }}
                      >
                        {config.selectedPlatforms.join(', ')}
                      </motion.span>
                    )}
                    {config.filters.grayscale && (
                      <motion.span
                        className={styles['config-tag']}
                        whileHover={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#ef4444' }}
                        transition={{ duration: 0.15 }}
                      >
                        B&W
                      </motion.span>
                    )}
                    <span className={styles['config-format-count']}>
                      {config.selectedFormats.length} format{config.selectedFormats.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <span className={styles['config-chevron']} aria-hidden="true">›</span>
                {canManage && (
                  <motion.button
                    onClick={(e) => { e.stopPropagation(); handleDelete(config._id!); }}
                    className={styles['delete-btn']}
                    whileHover={{ scale: 1.1 }}
                    whileTap={clayTapScale}
                  >
                    Delete
                  </motion.button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
      </div>

      {/* Preset details */}
      <Modal
        isOpen={!!detailConfig}
        onClose={() => setDetailConfig(null)}
        title={detailConfig?.name}
        size="md"
      >
        {detailConfig && (
          <div className={styles['detail']}>
            {/* Branding overlay */}
            <section className={styles['detail-section']}>
              <h4 className={styles['detail-section-title']}>Branding overlay</h4>
              {detailConfig.overlay ? (
                <div className={styles['detail-overlay']}>
                  <div className={styles['detail-overlay-preview']}>
                    <img src={overlaysApi.fileUrl(detailConfig.overlay.overlayId)} alt="" />
                  </div>
                  <div className={styles['detail-overlay-meta']}>
                    <p className={styles['detail-overlay-name']}>
                      {overlayById.get(detailConfig.overlay.overlayId)?.name ?? 'Overlay'}
                    </p>
                    <div className={styles['detail-kv-grid']}>
                      <div className={styles['detail-kv']}>
                        <span className={styles['detail-k']}>Position</span>
                        <span className={styles['detail-v']}>{prettyAnchor(detailConfig.overlay.anchor)}</span>
                      </div>
                      <div className={styles['detail-kv']}>
                        <span className={styles['detail-k']}>Size</span>
                        <span className={styles['detail-v']}>{OVERLAY_SIZE_LABELS[detailConfig.overlay.size]}</span>
                      </div>
                      <div className={styles['detail-kv']}>
                        <span className={styles['detail-k']}>Opacity</span>
                        <span className={styles['detail-v']}>{Math.round(detailConfig.overlay.opacity * 100)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className={styles['detail-empty']}>No overlay applied</p>
              )}
            </section>

            {/* Adjustments */}
            <section className={styles['detail-section']}>
              <h4 className={styles['detail-section-title']}>Adjustments</h4>
              <div className={styles['detail-kv-grid']}>
                <div className={styles['detail-kv']}>
                  <span className={styles['detail-k']}>Exposure</span>
                  <span className={styles['detail-v']}>{fmtSigned(detailConfig.filters.brightness)}</span>
                </div>
                <div className={styles['detail-kv']}>
                  <span className={styles['detail-k']}>Contrast</span>
                  <span className={styles['detail-v']}>{fmtSigned(detailConfig.filters.contrast)}</span>
                </div>
                <div className={styles['detail-kv']}>
                  <span className={styles['detail-k']}>Saturation</span>
                  <span className={styles['detail-v']}>{fmtSigned(detailConfig.filters.saturation)}</span>
                </div>
                <div className={styles['detail-kv']}>
                  <span className={styles['detail-k']}>Black &amp; white</span>
                  <span className={styles['detail-v']}>{detailConfig.filters.grayscale ? 'On' : 'Off'}</span>
                </div>
              </div>
            </section>

            {/* Platforms */}
            <section className={styles['detail-section']}>
              <h4 className={styles['detail-section-title']}>Platforms</h4>
              {detailConfig.selectedPlatforms.length > 0 ? (
                <div className={styles['detail-chips']}>
                  {detailConfig.selectedPlatforms.map((p) => (
                    <span key={p} className={styles['detail-chip']}>{p}</span>
                  ))}
                </div>
              ) : (
                <p className={styles['detail-empty']}>None selected</p>
              )}
            </section>

            {/* Export formats */}
            <section className={styles['detail-section']}>
              <h4 className={styles['detail-section-title']}>
                Export formats ({detailConfig.selectedFormats.length})
              </h4>
              {detailConfig.selectedFormats.length > 0 ? (
                <div className={styles['detail-chips']}>
                  {detailConfig.selectedFormats.map((f) => (
                    <span key={f} className={styles['detail-chip']}>{f}</span>
                  ))}
                </div>
              ) : (
                <p className={styles['detail-empty']}>None selected</p>
              )}
            </section>

            {detailConfig.createdAt && (
              <p className={styles['detail-created']}>
                Saved {new Date(detailConfig.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric',
                })}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
